<style scoped>
.user-filters {
	display: flex;
	gap: 0.5rem;
	align-items: center;
}

.user-filters input[type="search"] {
	flex: 1;
	min-width: 0;
	padding: 0.5rem;
	border: 1px solid #ddd;
	border-radius: 4px;
}

.user-filters button {
	padding: 0.5rem 1rem;
	background: #dc3545;
	color: white;
	border: none;
	border-radius: 4px;
	cursor: pointer;
}
</style>

<template>
	<ListCard
		:items="filteredContacts"
		:loading="loading"
		selection-mode="single"
		@selection-change="handleSelectionChange"
	>
		<!-- Filters Slot -->
		<template #filters>
			<div class="user-filters">
				<input
					v-model="searchTerm"
					type="search"
					placeholder="Search name, email, phone or address"
				/>
				<button v-if="searchTerm !== ''" @click="clearSearch">Clear</button>
			</div>
		</template>

		<!-- Item Slot -->
		<template #item="{ item, isSelected, toggleSelection }">
			<ListCardContact
				:contact="item"
				@click="toggleSelection"
				:is-selected="isSelected"
			/>
		</template>
	</ListCard>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";

import ListCard from "@/components/layout/ListCard.vue";
import ListCardContact from "@/components/ListCardContact.vue";
import {
	formatContactAddress,
	formatContactReportedAddress,
	usPhoneDigits,
} from "@/format";
import {
	Contact,
	ContactAddressDTO,
	ContactReportedAddressDTO,
} from "@/type/api";

interface Props {
	contacts: Contact[] | undefined;
	loading: boolean;
}
interface Emits {
	"selection-change": [contact: Contact | undefined];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const searchTerm = ref("");

// The one search box matches a contact by name, by any of its email
// addresses, by the street line of any Site it is linked to, by the address of
// any public report it filed, or by phone number. Phone matching only runs for
// a phone-shaped term — digits plus the punctuation a person types into a
// number — so a name search can never light up a row through its digits. Both
// sides of the phone comparison fold to the same national digits, which is
// what makes "5305551" and "(530) 555-1" reach a number stored either way.
const phoneShaped = /^[\d\s()+\-.]+$/;

const addressLine = (address: ContactAddressDTO): string =>
	formatContactAddress(address).toLowerCase();

// Shares the formatter with the detail card, so a row matched here shows the
// very words that matched it — including a report that carries no resolved
// address and only its raw text.
const reportedLine = (address: ContactReportedAddressDTO): string =>
	formatContactReportedAddress(address).toLowerCase();

const matchesSearch = (contact: Contact, term: string): boolean => {
	if (term === "") {
		return true;
	}
	if (contact.name.toLowerCase().includes(term)) {
		return true;
	}
	if (contact.emails.some((email) => email.toLowerCase().includes(term))) {
		return true;
	}
	if (
		contact.addresses.some((address) => addressLine(address).includes(term))
	) {
		return true;
	}
	if (
		contact.reported_addresses.some((address) =>
			reportedLine(address).includes(term),
		)
	) {
		return true;
	}
	if (phoneShaped.test(term) && /\d/.test(term)) {
		// Both sides fold the same way, so a US number stored bare ("3233566326")
		// and one stored E.164 ("+1 323-356-6326") answer to the same digits. A
		// stored number that is not a US one keeps its own digits, so search still
		// reaches an international row.
		const digits = usPhoneDigits(term) ?? term.replace(/\D/g, "");
		return contact.phones.some((phone) =>
			(usPhoneDigits(phone.e164) ?? phone.e164.replace(/\D/g, "")).includes(
				digits,
			),
		);
	}
	return false;
};

// The visible set is computed here rather than pushed into ListCard: its
// applyFilter appends a predicate per call, so every keystroke added another
// entry to the filter array and emptying the box never removed them — the list
// stayed narrowed by a filter the header no longer showed. A computed keeps the
// rows a function of the current term and nothing else.
const filteredContacts = computed((): Contact[] => {
	const term = searchTerm.value.trim().toLowerCase();
	return (props.contacts ?? []).filter((contact) =>
		matchesSearch(contact, term),
	);
});

const clearSearch = () => {
	searchTerm.value = "";
};

const handleSelectionChange = (selectedContacts: Contact[]) => {
	emit("selection-change", selectedContacts[0]);
};
</script>
